/**
 * InfoValue: renderiza un valor con enmascarado opcional según patrón.
 *
 * Sprint 14 S14-06b. Resuelve D-S14-6: el Dashboard de Inicio enmascara
 * datos sensibles (RUT, email, celular) porque el vecino puede tener
 * gente alrededor mirando; Mis Datos los muestra completos porque el
 * vecino entró deliberadamente a verlos.
 *
 * El patrón de enmascarado lo controla la página, NO el componente. La
 * misma persona se renderiza así:
 *
 *   <InfoValue value={persona.rut} mask="rut" />       // Dashboard
 *   <InfoValue value={persona.rut} />                  // Mis Datos
 *
 * Cuando `value` es null/undefined/string vacío, renderiza un guión
 * (`—`) en color muted para indicar ausencia sin ambigüedad.
 *
 * Mascaras soportadas:
 * - `rut`:     `12345678-9`           → `1.***.***-9`
 * - `email`:   `alguien@correo.cl`    → `a.••••@correo.cl`
 * - `celular`: `912345678`            → `+56 9 1*** **78`
 *
 * Doctrina del celular (Sprint 14 S14-Cel): el valor canónico almacenado
 * son 9 dígitos chilenos empezando con `9`. El prefijo `+56` se renderiza
 * en la UI como prefijo visual fijo (Chile). El enmascarado preserva
 * los 2 primeros dígitos (`91`) y los 2 últimos (`78`), oculta los 5
 * del medio. Resultado con grupos chilenos: `+56 9 1*** **78`.
 */
import type { ReactNode } from 'react';
import { formatRut, formatCelular } from '../../lib/format';

type MaskType = 'rut' | 'email' | 'celular';
type FormatType = 'rut' | 'celular';

interface InfoValueProps {
  value: string | null | undefined;
  /** Si false o no se pasa, no enmascara. */
  mask?: MaskType | false;
  /**
   * Formato de display cuando NO se enmascara. Doctrina RUT/celular: la BD
   * guarda canónico (RUT sin puntos, celular 9 dígitos) y la UI muestra
   * formateado (RUT `12.345.678-5`, celular `+569.1234.5678`). Si se pasa
   * `mask`, este formato se ignora (la máscara ya da su propio formato).
   */
  format?: FormatType | false;
  /** Render cuando value está vacío (default: guión). */
  emptyFallback?: ReactNode;
  className?: string;
}

export function InfoValue({
  value,
  mask = false,
  format = false,
  emptyFallback,
  className = '',
}: InfoValueProps) {
  if (!value || value.trim() === '') {
    return (
      <span className={`text-text-muted ${className}`.trim()}>
        {emptyFallback ?? '—'}
      </span>
    );
  }

  let display: string;
  if (mask) {
    display = maskValue(value, mask);
  } else if (format === 'rut') {
    display = formatRut(value);
  } else if (format === 'celular') {
    display = formatCelular(value);
  } else {
    display = value;
  }

  return <span className={className.trim() || undefined}>{display}</span>;
}

/**
 * Aplica el patrón de enmascarado al valor.
 * Exportado para usar fuera del componente (p.ej. para `aria-label` o
 * tests que comparan el output esperado).
 */
export function maskValue(value: string, mask: MaskType): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;

  switch (mask) {
    case 'rut':
      return maskRut(trimmed);
    case 'email':
      return maskEmail(trimmed);
    case 'celular':
      return maskCelular(trimmed);
  }
}

function maskRut(rut: string): string {
  // Acepta formatos con o sin puntos/guión: `12345678-9`, `12.345.678-9`,
  // `123456789`. Mantiene primer dígito + dígito verificador (último).
  // Ejemplo: `1.***.***-9`.
  const clean = rut.replace(/[^0-9kK]/g, '');
  if (clean.length < 2) return rut; // muy corto, no enmascarar

  const first = clean[0];
  const verifier = clean[clean.length - 1];
  return `${first}.***.***-${verifier}`;
}

function maskEmail(email: string): string {
  const at = email.indexOf('@');
  if (at <= 0) return email; // sin @ válida, no enmascarar
  const localPart = email.slice(0, at);
  const domain = email.slice(at);
  const firstChar = localPart[0] ?? '';
  return `${firstChar}.••••${domain}`;
}

function maskCelular(celular: string): string {
  // Doctrina S14-Cel: input canónico = 9 dígitos chilenos (912345678).
  // Mantiene 2 primeros + 2 últimos visibles, oculta los 5 del medio.
  // Output formateado en grupos chilenos: `+56 9 1*** **78`.
  const digits = celular.replace(/\D/g, '');
  if (digits.length === 9 && digits.startsWith('9')) {
    const d2 = digits[1];        // primer dígito post-9
    const d8 = digits[7];        // antepenúltimo
    const d9 = digits[8];        // último
    return `+56 9 ${d2}*** **${d8}${d9}`;
  }
  // Defensivo: input fuera del canon (legacy con `+56`, formato no
  // chileno, etc.) — devuelve el valor crudo para que sea visible que
  // hay un dato mal-formado en el sistema, en vez de inventar un mask.
  return celular;
}
